/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { Button, Modal, Text, Textarea, UnstyledButton } from '@/shared/ui';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';

type Translate = (key: string) => string;

/**
 * Uppercase field label styled with the canonical meta typography tokens.
 *
 * @param children - The label text.
 * @returns The rendered label element.
 */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      component="label"
      className="text-[length:var(--text-meta)] font-medium tracking-[var(--tracking-wide)] text-(--text-secondary)"
    >
      {children}
    </Text>
  );
}

export type FeedbackDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rating: number;
  onRatingChange: (rating: number) => void;
  obs: string;
  onObsChange: (obs: string) => void;
  onConfirm: () => void;
  t: Translate;
};

/** Visit "done" feedback modal: star rating + broker notes. */
export const VisitFeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open, onOpenChange, rating, onRatingChange, obs, onObsChange, onConfirm, t,
}) => (
  <Modal opened={open} onClose={() => onOpenChange(false)} title={t('visits.feedback.title')} size="sm">
    <div className="flex flex-col gap-(--space-4)">
      <div className="flex flex-col gap-(--space-1)">
        <FieldLabel>{t('visits.feedback.rating')}</FieldLabel>
        <div className="flex gap-(--space-1)">
          {[1, 2, 3, 4, 5].map((star) => (
            <UnstyledButton
              key={star}
              type="button"
              onClick={() => onRatingChange(star)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: star <= rating ? 'var(--seed-base)' : 'var(--border-subtle)', padding: 0, lineHeight: 1 }}
            >
              &#9733;
            </UnstyledButton>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-(--space-1)">
        <FieldLabel>{t('visits.feedback.brokerNotes')}</FieldLabel>
        <Textarea value={obs} onChange={(e) => onObsChange(e.target.value)} rows={3} maxLength={FORM_FIELD_LIMITS.notes} />
      </div>
      <div className="flex justify-end gap-(--space-2)">
        <Button variant="subtle" type="button" onClick={() => onOpenChange(false)}>{t('visits.feedback.cancel')}</Button>
        <Button type="button" onClick={onConfirm}>{t('visits.feedback.confirm')}</Button>
      </div>
    </div>
  </Modal>
);

export type CancelDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  t: Translate;
};

/** Visit cancellation modal: reason text area. */
export const VisitCancelDialog: React.FC<CancelDialogProps> = ({
  open, onOpenChange, reason, onReasonChange, onConfirm, t,
}) => (
  <Modal opened={open} onClose={() => onOpenChange(false)} title={t('visits.cancel.title')} size="sm">
    <div className="flex flex-col gap-(--space-4)">
      <div className="flex flex-col gap-(--space-1)">
        <FieldLabel>{t('visits.cancel.reasonLabel')}</FieldLabel>
        <Textarea value={reason} onChange={(e) => onReasonChange(e.target.value)} rows={3} maxLength={FORM_FIELD_LIMITS.cancelReason} />
      </div>
      <div className="flex justify-end gap-(--space-2)">
        <Button variant="subtle" type="button" onClick={() => onOpenChange(false)}>{t('visits.cancel.back')}</Button>
        <Button type="button" onClick={onConfirm}>{t('visits.cancel.confirm')}</Button>
      </div>
    </div>
  </Modal>
);
